// services/purchaseOrder.js
import axios from 'axios';
import { URLEndpoints } from '../../constants/urlConstants';

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;

/* --------------------------- auth header helper --------------------------- */
function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { accessToken: token } : {};
}

/* ----------------------- GeneratedPurchaseOrder cache ---------------------- */
const TTL = 5 * 60 * 1000; // 5 minutes
export const poCacheKey = (id) => `po:${id}`;

// read
export function readPurchaseOrderCache(id) {
  try {
    const raw = sessionStorage.getItem(poCacheKey(id));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || !data) return null;
    if (Date.now() - ts > TTL) return null;
    return data;
  } catch {
    return null;
  }
}

// write
export function writePurchaseOrderCache(id, data) {
  try {
    sessionStorage.setItem(poCacheKey(id), JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

// invalidate (this tab)
export function invalidatePurchaseOrderCache(id) {
  try {
    sessionStorage.removeItem(poCacheKey(id));
  } catch {}
}

// cross-tab publish/subscribe
const topic = (id) => `po:invalidate:${id}`;

// tell other tabs
export function publishPurchaseOrderInvalidation(id) {
  try {
    window.dispatchEvent(new CustomEvent('po-invalidate', { detail: { id: String(id) } }));
    localStorage.setItem(topic(id), String(Date.now()));
    // immediately clean it so storage doesn't bloat
    localStorage.removeItem(topic(id));
  } catch {}
}

// listen in the public page
export function subscribePurchaseOrderInvalidation(id, cb) {
  const sid = String(id);
  const handler = (e) => {
    if (e.key === topic(id)) cb?.();
  };
  const sameTabHandler = (e) => {
    const eventId = String(e?.detail?.id || '');
    if (eventId && eventId === sid) cb?.();
  };
  window.addEventListener('storage', handler);
  window.addEventListener('po-invalidate', sameTabHandler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('po-invalidate', sameTabHandler);
  };
}

/* --------------------------------- APIs ---------------------------------- */

export const createPurchaseOrderApi = async (id) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/create-purchase-order`,
      {},
      { headers: authHeaders() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const fetchOutstandingPurchaseOrders = async (id) => {
  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/outstanding-purchase-orders`,
    { headers: authHeaders() }
  );
  return res.data;
};

export const createSelectedOutstandingPurchaseOrders = async (id, outstandingGroupKeys) => {
  const res = await axios.post(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/create-purchase-order`,
    { outstandingGroupKeys },
    { headers: authHeaders() }
  );
  return res.data;
};

export const fetchPurchaseOrderProofPreflight = async (id) => {
  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/proof-preflight`,
    { headers: authHeaders() }
  );
  return res?.data;
};

export const fetchAllPurchaseOrders = async (id) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/${URLEndpoints.purchaseOrders}`,
      { headers: authHeaders() }
    );
    console.log(res.data.purchaseOrders);
    return res.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const fetchArchivedPurchaseOrdersCount = async (salesOrderId, instanceUniqueId) => {
  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${salesOrderId}/archived-purchase-orders/count`,
    { headers: authHeaders(), params: { instanceUniqueId } }
  );
  return Number(res?.data?.count) || 0;
};

export const fetchArchivedPurchaseOrders = async (salesOrderId, instanceUniqueId) => {
  const res = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${salesOrderId}/archived-purchase-orders`,
    { headers: authHeaders(), params: { instanceUniqueId } }
  );
  return res?.data?.purchaseOrders || [];
};

export const restoreArchivedPurchaseOrder = async (salesOrderId, poId, instanceUniqueId) => {
  const { data } = await axios.post(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${salesOrderId}/archived-purchase-orders/${poId}/restore`,
    {},
    { headers: authHeaders(), params: { instanceUniqueId } }
  );
  return data;
};

export const fetchAllProofsList = async (id) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/${URLEndpoints.proofsList}`,
      { headers: authHeaders() }
    );
    console.log(res);
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const fetchAllDesignReps = async (id) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${URLEndpoints.getDesigners}`,
      { headers: authHeaders() }
    );
    console.log(res);
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

// upload proofs (multipart)
export const uploadProofList = async (poId, formData) => {
  if (!poId) throw new Error('uploadProofList: missing poId');

  // Requires: URLEndpoints.uploadProof = "upload-proof"
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/${poId}/${URLEndpoints.uploadProof}`;

  try {
    const res = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authHeaders(),
      },
    });
    return res?.data; // { message, proofs }
  } catch (err) {
    console.error('uploadProofList failed:', err?.response?.data || err);
    throw err; // propagate to caller
  }
};

export const approveProofList = async (id, value) => {
  try {
    const res = await axios.put(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/${URLEndpoints.updateProof}`,
      value,
      { headers: authHeaders() }
    );
    console.log(res);
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export async function fetchProductionNotes(orderId) {
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/note/by-order/${orderId}`;
  const { data } = await axios.get(url, {
    headers: authHeaders(),
  });
  return Array.isArray(data?.notes) ? data.notes : [];
}

export async function addProductionNoteByPO(poId, payload) {
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/note/by-po/${poId}`;
  const body =
    typeof payload === "string"
      ? { message: payload }
      : { ...payload };
  const { data } = await axios.post(
    url,
    body,
    { headers: authHeaders() }
  );
  return data?.note ? { ...data.note, note: data.note, ok: data.ok } : data;
}

export async function pinProductionNoteByOrder(orderId, noteId, pinned) {
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/note/by-order/${orderId}/${noteId}/pin`;
  const { data } = await axios.patch(url, { pinned }, { headers: authHeaders() });
  return data;
}

export async function replyProductionNoteByOrder(orderId, noteId, message) {
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/note/by-order/${orderId}/${noteId}/reply`;
  const { data } = await axios.post(url, { message }, { headers: authHeaders() });
  return data;
}

export async function deleteProductionNoteByOrder(orderId, noteId) {
  const url = `${BASE_URL}/${URLEndpoints.purchaseOrder}/note/by-order/${orderId}/${noteId}`;
  const { data } = await axios.delete(url, { headers: authHeaders() });
  return data;
}

export const fetchClientProof = async (poId, proofId) => {
  const { data } = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${poId}/proofs/${proofId}`,
    { headers: authHeaders() }
  );
  return data;
};

export const fetchClientProofById = async (proofId) => {
  const { data } = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/proofs/${proofId}/client-view`,
    { headers: authHeaders() }
  );
  return data;
};

export const lookupPurchaseOrders = async ({
  instanceUniqueId,
  purchaseOrder,
  salesOrder,
  projectNumber,
  signal,
}) => {
  const headers = authHeaders();

  const params = {
    // bandaid backend expects these exact keys:
    po: purchaseOrder,
    salesOrder,
    project: projectNumber,
    // optional instance scope (ignored if your API doesn’t need it)
    instanceUniqueId,
  };

  // drop undefined params (keeps URL clean)
  Object.keys(params).forEach((k) => params[k] == null && delete params[k]);

  const res = await axios.get(`${BASE_URL}/purchase-order`, {
    params,
    headers,
    signal,
  });

  return res.data; // { purchaseOrders: [...] }
};

export const fetchClientProofBundle = async (poId) => {
  const { data } = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${poId}/client-proofs`,
    { headers: authHeaders() }
  );
  return data;
};

export const fetchClientProofBundleByOrder = async (orderId) => {
  const { data } = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/by-order/${orderId}/client-proofs`,
    { headers: authHeaders() }
  );
  return data;
};

/**
 * Submits an approval/rejection for a proof.
 * Keeps the original endpoint + name so submitDecision() doesn't change.
 * Backend should infer "internal vs client" from the token we send here.
 */
export const setClientProofApproval = async (poId, proofId, payload) => {
  const { data } = await axios.patch(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${poId}/proofs/${proofId}/client-approval`,
    payload,
    { headers: authHeaders() }
  );
  return data;
};

export const fetchPurchaseOrderDetails = async (id) => {
  const apiBaseUrl = process.env.REACT_APP_NODEJS_SERVER_URL || "";
  const requestUrl = `${apiBaseUrl}/${URLEndpoints.purchaseOrder}/${id}`;
  const renderParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("render")
      : null;

  console.log("[generated-po:actual-request]", {
    poId: id,
    requestUrl,
    apiBaseUrl,
    frontendOrigin:
      typeof window !== "undefined" ? window.location.origin : null,
    render: renderParam,
  });

  try {
    const res = await axios.get(requestUrl, {
      headers: {
        ...authHeaders(),
        "Cache-Control": "no-cache",
      },
      params: {
        render: renderParam,
      },
    });
    console.log("[generated-po:actual-response]", {
      poId: id,
      requestUrl,
      status: res.status,
      itemCount: Array.isArray(res?.data?.purchaseOrder?.items)
        ? res.data.purchaseOrder.items.length
        : null,
      backendBuildSha:
        res.headers?.["x-elite-core-build-sha"] || null,
      endpoint:
        res.headers?.["x-elite-core-generated-po-endpoint"] || null,
    });
    return res?.data;
  } catch (error) {
    console.error("[generated-po:actual-request-failed]", {
      poId: id,
      requestUrl,
      message: error.message,
      status: error.response?.status ?? null,
      responseData: error.response?.data ?? null,
    });
    return Promise.reject(error);
  }
};

export const updatePurchaseOrderDetails = async (id, payload) => {
  try {
    const res = await axios.put(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}`,
      payload,
      { headers: authHeaders() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const initiateBinatedProof = async (id, payload = null) => {
  const { data } = await axios.post(
    `${BASE_URL}/binated/purchase-orders/${id}/start-official-proof`,
    payload ? { payload } : {},
    { headers: authHeaders() }
  );
  return data;
};

export const refreshBinatedProofStatus = async (id) => {
  const { data } = await axios.get(
    `${BASE_URL}/binated/purchase-orders/${id}/status`,
    { headers: authHeaders() }
  );
  return data;
};

export const updateBinatedProofOrder = async (id, payload) => {
  const { data } = await axios.put(
    `${BASE_URL}/binated/purchase-orders/${id}/update-order`,
    { payload },
    { headers: authHeaders() }
  );
  return data;
};

export const requestBinatedProofRevision = async (id, payload = {}) => {
  const { data } = await axios.put(
    `${BASE_URL}/binated/purchase-orders/${id}/update-order`,
    { payload: { ...(payload || {}), revisionRequest: true } },
    { headers: authHeaders() }
  );
  return data;
};

export const deletePurchaseOrderApi = async (id) => {
  try {
    const res = await axios.delete(`${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}`, {
      headers: authHeaders(),
    });
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const sendMailToSupplier = async (id, data = {}) => {
  console.log('[sendMailToSupplier] POST', { id, data }); // quick debug
  const res = await axios.post(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/send-supplier-mail/${id}`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      // withCredentials: true, // enable only if your API uses cookies
    }
  );
  return res?.data;
};

export const previewPurchaseOrderPdf = async (id) => {
  const res = await axios.post(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/${id}/pdf-preview`,
    {},
    { headers: authHeaders() }
  );
  return res?.data;
};

export const requestProofs = async (payload) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/request-proof`,
      payload,
      { headers: authHeaders() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const requestArtworkForDocument = async (formData) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/request-artwork`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...authHeaders(),
        },
      }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const regeneratePurchaseOrder = async (payload) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/regenerate-purchase-order`,
      payload,
      { headers: authHeaders() }
    );
    return res?.data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const getProductComments = async (PurchaseOrderId, productId) => {
  try {
    const response = await axios.get(`${BASE_URL}/PurchaseOrder-comment/`, {
      params: {
        PurchaseOrderId: PurchaseOrderId || 94,
        productId: productId || 553546514,
      },
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error('Error fetching comments');
  }
};

export const editComment = async (commentId, updatedComment) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/PurchaseOrder-comment/${commentId}`,
      { comment: updatedComment },
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error editing comment:', error);
    throw error;
  }
};

// Delete comment function
export const deleteComment = async (commentId) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/PurchaseOrder-comment/${commentId}`,
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

// approve the purchase order
export const approvePurchaseOrder = async (PurchaseOrderId, approveStatus) => {
  try {
    console.log(PurchaseOrderId, approveStatus, 'my approvedPurchaserID');
    const response = await axios.put(
      `${BASE_URL}/${URLEndpoints.purchaseOrder}/${PurchaseOrderId}/approve-order`,
      { approveStatus },
      { headers: authHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error approving order', error);
    return Promise.reject(error);
  }
};

export const fetchProjectEmailAddress = async (orderId) => {
  const { data } = await axios.get(
    `${BASE_URL}/${URLEndpoints.purchaseOrder}/project-email/by-order/${orderId}`,
    { headers: authHeaders() }
  );
  return data;
};
